import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'മലയാളം' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' },
  { code: 'ur', label: 'اردو' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ar', label: 'العربية' },
];

const CHIPS_BY_LANG = {
  en: [
    'Why does intimacy feel uncomfortable lately?',
    'How can I improve my libido naturally?',
    'What helps with vaginal dryness?',
    'I have low confidence in bed — what do I do?',
    'Tell me about pelvic floor strengthening',
    'Is painful sex ever normal?',
    'My desire has dropped after childbirth — help',
    'How do I talk to my partner about intimacy?',
  ],
  hi: [
    'अंतरंगता असहज क्यों लगती है?',
    'कामेच्छा प्राकृतिक रूप से कैसे बढ़ाऊं?',
    'योनि की शुष्कता के लिए क्या करें?',
    'पेल्विक फ्लोर मजबूत करने के बारे में बताइए',
    'दर्दनाक संबंध — क्या यह सामान्य है?',
  ],
  mr: [
    'अंतरंग जीवनाबाबत प्रश्न आहे',
    'कामेच्छा कमी का झाली?',
    'पेल्विक फ्लोर मजबुतीसाठी काय करावे?',
  ],
};

// Clean rendering: keep markdown headers as gold sub-heads, render bullets, bold and italics
// Also auto-link any of the 5 INTIVIV™ product mentions to the store.
const STORE_URL = 'https://www.intiviv.com';
// Order matters: match longer / more specific names first so "Intimate Comfort Moisturizer"
// is matched before the looser "Moisturizer".
const PRODUCT_PATTERNS = [
  // Full INTIVIV™-prefixed forms
  /INTIVIV(?:™|\u2122|<sup>TM<\/sup>)?\s+Intimate\s+Balance\s+Cleanser/gi,
  /INTIVIV(?:™|\u2122|<sup>TM<\/sup>)?\s+Intimate\s+Silk\s+Lubricant/gi,
  /INTIVIV(?:™|\u2122|<sup>TM<\/sup>)?\s+Intimate\s+Comfort\s+Moisturizer/gi,
  /INTIVIV(?:™|\u2122|<sup>TM<\/sup>)?\s+Sensual\s+Massage\s+Oil/gi,
  /INTIVIV(?:™|\u2122|<sup>TM<\/sup>)?\s+Sensual\s+Intimate\s+Oil/gi,
  /INTIVIV(?:™|\u2122|<sup>TM<\/sup>)?\s+Intimate\s+Freshness\s+Mist/gi,
  // Looser fallbacks (no INTIVIV prefix) — VIVI often refers by short name in caps
  /\bIntimate\s+Balance\s+Cleanser\b/gi,
  /\bIntimate\s+Silk\s+Lubricant\b/gi,
  /\bIntimate\s+Comfort\s+Moisturi[zs]er\b/gi,
  /\bSensual\s+Massage\s+Oil\b/gi,
  /\bSensual\s+Intimate\s+Oil\b/gi,
  /\bIntimate\s+Freshness\s+Mist\b/gi,
];

function linkifyProducts(html) {
  if (!html) return html;
  let out = html;
  for (const re of PRODUCT_PATTERNS) {
    out = out.replace(re, (m) =>
      `<a href="${STORE_URL}" target="_blank" rel="noopener noreferrer" class="vivi-product-link" data-testid="vivi-product-link">${m}<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i></a>`
    );
  }
  return out;
}

function formatMsg(text) {
  if (!text) return '';
  let cleaned = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^---+\s*$/gm, '')          // strip horizontal rules
    .replace(/^\*\*\*+\s*$/gm, '')       // strip *** rules
    .replace(/```[\s\S]*?```/g, '')      // strip code blocks
    .replace(/`([^`]+)`/g, '$1');        // strip inline code

  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');

  const lines = cleaned.split('\n');
  let html = '', inList = false;
  const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };

  for (const raw of lines) {
    const t = raw.trim();
    if (!t) { closeList(); continue; }

    // Markdown header → gold subhead
    const headerMatch = t.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      closeList();
      const level = headerMatch[1].length;
      const cls = level <= 2 ? 'vivi-h1' : 'vivi-h2';
      html += `<div class="${cls}">${inline(headerMatch[2])}</div>`;
      continue;
    }

    const isBullet = /^([•\-\*])\s+/.test(t);
    if (isBullet) {
      if (!inList) { html += '<ul>'; inList = true; }
      const inner = inline(t.replace(/^[•\-\*]\s+/, ''));
      html += `<li>${inner}</li>`;
    } else {
      closeList();
      html += `<p>${inline(t)}</p>`;
    }
  }
  closeList();
  return linkifyProducts(html);
}

export default function AiCoach() {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [showBurnConfirm, setShowBurnConfirm] = useState(false);
  const [language, setLanguage] = useState(() => localStorage.getItem('intiviv_lang') || 'en');
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  // Map<message_id, journal_entry_id> so the chat bookmark can toggle (save → unsave) without leaving the page.
  const [savedJournalIds, setSavedJournalIds] = useState({});
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  // Scroll so the START of the latest VIVI answer is visible (not the bottom).
  const scrollToLatest = () => {
    // Nothing to scroll to when chat is empty — avoid yanking the topbar
    // off-screen on the empty state.
    if (!messages || messages.length === 0) return;
    requestAnimationFrame(() => {
      // While streaming/loading, keep view near user message; once VIVI replies, align to top of last assistant bubble.
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant') {
        const el = document.getElementById(`msg-${last.id}`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  };
  useEffect(scrollToLatest, [messages, loading]);

  // Pick up a seed question (e.g. from an Intimacy Journey day) and auto-send once.
  useEffect(() => {
    let seed = null;
    try { seed = sessionStorage.getItem('vivi_seed'); } catch (e) {}
    if (seed) {
      try { sessionStorage.removeItem('vivi_seed'); } catch (e) {}
      setTimeout(() => send(seed), 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = (code) => {
    setLanguage(code);
    localStorage.setItem('intiviv_lang', code);
    setShowLangPicker(false);
  };

  const autoResize = () => {
    const ta = textareaRef.current;
    if (ta) { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'; }
  };

  const send = useCallback(async (msgText) => {
    const text = (msgText || input).trim();
    if (!text || loading) return;
    setInput('');
    if (textareaRef.current) { textareaRef.current.style.height = 'auto'; }

    const userMsg = { role: 'user', content: text, id: Date.now() };
    setMessages(m => [...m, userMsg]);
    setLoading(true);

    try {
      const r = await axios.post(`${API}/api/chat/send`,
        { message: text, session_id: sessionId, language },
        { headers: authHeader() }
      );
      setMessages(m => [...m, { role: 'assistant', content: r.data.response, id: Date.now() + 1 }]);
    } catch {
      toast.error('VIVI is unavailable right now. Please try again.');
    } finally { setLoading(false); }
  }, [input, loading, sessionId, authHeader, language]);

  const burnConversation = async () => {
    try {
      await axios.delete(`${API}/api/chat/history`, { headers: authHeader() });
      setMessages([]);
      setShowBurnConfirm(false);
      toast.success('Conversation deleted permanently.');
    } catch { toast.error('Could not delete. Please try again.'); }
  };

  // ── Voice input (STT) ──
  const startRecording = async () => {
    // Pre-flight checks: secure context + API availability
    if (!window.isSecureContext) {
      toast.error('Voice requires a secure (HTTPS) connection.');
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Your browser does not support voice input. Try Chrome or Safari.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) { toast.error('Recording too short.'); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'voice.webm');
          const r = await axios.post(`${API}/api/voice/stt`, fd, {
            headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
          });
          if (r.data?.text) {
            await send(r.data.text);
          } else {
            toast.error('Could not transcribe audio.');
          }
        } catch {
          toast.error('Voice transcription failed.');
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      const name = err?.name || '';
      // Detect platform for tailored instructions
      const ua = navigator.userAgent || '';
      const isiOS = /iPhone|iPad|iPod/i.test(ua);
      const isAndroid = /Android/i.test(ua);
      const isMobileWeb = isiOS || isAndroid;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        const tip = isiOS
          ? 'iOS: Settings → Safari → Microphone → Allow, then reload INTIVIV.'
          : isAndroid
          ? 'Android: tap the lock icon left of the URL → Permissions → Microphone → Allow, then reload.'
          : 'Desktop: click the lock/info icon left of the URL → Site settings → Microphone → Allow, then reload.';
        toast.error(`Microphone permission blocked. ${tip}`, { duration: 9000 });
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        toast.error('No microphone detected on this device. Please connect a mic and try again.');
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        toast.error('Your mic is in use by another app (call, recorder, browser tab). Close it and retry.');
      } else if (name === 'SecurityError') {
        toast.error('Voice is blocked inside this in-app browser. Open INTIVIV in Chrome or Safari.');
      } else if (isMobileWeb && name === 'OverconstrainedError') {
        toast.error('Mic settings unsupported on this device. Try a different browser.');
      } else {
        toast.error('Could not access microphone. Please check permissions and try again.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  // ── Voice output (TTS) ──
  const speakMessage = async (msg) => {
    try {
      if (playingId === msg.id) {
        audioRef.current?.pause();
        setPlayingId(null);
        return;
      }
      setPlayingId(msg.id);
      const r = await axios.post(`${API}/api/voice/tts`, { text: msg.content }, { headers: authHeader() });
      const audioSrc = `data:audio/mpeg;base64,${r.data.audio_base64}`;
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
      await audio.play();
    } catch {
      toast.error('Voice playback failed.');
      setPlayingId(null);
    }
  };

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  const chips = CHIPS_BY_LANG[language] || CHIPS_BY_LANG.en;

  // Strip markdown / HTML from a VIVI reply for clean WhatsApp share text
  const cleanForShare = (s) => (s || '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const shareToWhatsApp = (m) => {
    const body = `*VIVI · INTIVIV™ wellness tip*\n\n${cleanForShare(m.content)}\n\n— via INTIVIV™, India's first Gynaecologist-Led AI Intimate Wellness Ecosystem`;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(body)}`;
    window.open(url, '_blank', 'noopener');
    axios.post(`${API}/api/track`, { event: 'vivi_share_whatsapp', label: m.id?.toString(), meta: { len: (m.content || '').length } }, { headers: authHeader() }).catch(() => {});
  };

  const saveToJournal = async (m) => {
    // Toggle: if already saved, delete; else save.
    if (savedIds.has(m.id)) {
      const journalId = savedJournalIds[m.id];
      if (!journalId) {
        // No id captured (e.g., previous session). Just clear local mark.
        setSavedIds(prev => { const next = new Set(prev); next.delete(m.id); return next; });
        toast.info('Removed from this view. Manage saved entries in Profile.');
        return;
      }
      try {
        await axios.delete(`${API}/api/journal/${journalId}`, { headers: authHeader() });
        setSavedIds(prev => { const next = new Set(prev); next.delete(m.id); return next; });
        setSavedJournalIds(prev => { const { [m.id]: _, ...rest } = prev; return rest; });
        toast.success('Removed from your Journal.');
      } catch {
        toast.error('Could not remove. Please try again.');
      }
      return;
    }
    try {
      const r = await axios.post(`${API}/api/journal`, {
        content: m.content,
        session_id: sessionId,
        source_message_id: String(m.id),
      }, { headers: authHeader() });
      setSavedIds(prev => new Set(prev).add(m.id));
      if (r.data?.id) setSavedJournalIds(prev => ({ ...prev, [m.id]: r.data.id }));
      toast.success('Saved to your Journal · view in Profile.');
    } catch {
      toast.error('Could not save. Please try again.');
    }
  };

  return (
    <div className="chat-shell">
      {/* Top bar */}
      <div className="chat-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/home'); }}
            title="Back"
            aria-label="Back"
            data-testid="chat-back-btn"
            style={{
              width: 32, height: 32, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--gold-dim)', border: '1px solid var(--border)',
              color: 'var(--gold)', cursor: 'pointer',
              fontSize: '0.87rem',
            }}
          >
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div className="vivi-badge">
            <div className="vivi-avatar">V</div>
            <div>
              <div className="vivi-name">VIVI</div>
              <div className="vivi-status" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div className="online-dot" />
                Personal Intimate Wellness Coach
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setShowLangPicker(true)}
            data-testid="lang-picker-btn"
            style={{
              fontSize: '0.73rem', fontWeight: 700, letterSpacing: '0.12em',
              color: 'var(--gold)', background: 'var(--gold-dim)',
              border: '1px solid var(--border)', padding: '4px 10px',
              cursor: 'pointer', fontFamily: 'var(--font-ui)',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <i className="fa-solid fa-globe" /> {currentLang.label}
          </button>
          <button className="btn-ghost" onClick={() => setShowBurnConfirm(true)} title="Burn this conversation" style={{ fontSize: '0.93rem', color: 'var(--text-dim)', padding: '4px 8px' }} data-testid="chat-burn-btn">
            <i className="fa-solid fa-fire" />
          </button>
        </div>
      </div>

      {/* Disclaimer — two lines */}
      <div style={{ padding: '8px 16px', background: 'rgba(212,175,55,0.03)', borderBottom: '1px solid var(--border-soft)', fontSize: '0.73rem', color: 'var(--text-dim)', textAlign: 'center', lineHeight: 1.6 }}>
        <div>
          <i className="fa-solid fa-shield-halved" style={{ color: 'var(--gold)', marginRight: 4 }} />
          VIVI provides wellness guidance only — not medical advice.
        </div>
        <div>Consult Gynaecologist for clinical care.</div>
      </div>

      {/* Messages */}
      <div className="messages-area" style={{ flex: 1 }}>
        {messages.length === 0 && (
          <div className="vivi-empty-shell" data-testid="vivi-empty-state">
            {/* Animated halo */}
            <div className="vivi-empty-halo" />
            <div className="vivi-empty-halo vivi-empty-halo-2" />

            {/* Headline — VIVI + Personal Intimate Wellness Coach */}
            <div className="vivi-empty-title shimmer-text">VIVI</div>
            <div className="vivi-empty-eyebrow">Personal Intimate Wellness Coach</div>

            <p className="vivi-empty-sub">
              Ask anything about your intimate wellness —<br />
              in complete privacy and confidence.
            </p>

            {/* Feature pills */}
            <div className="vivi-empty-pills">
              <div className="vivi-empty-pill"><i className="fa-solid fa-microphone" /> Voice mode</div>
              <div className="vivi-empty-pill"><i className="fa-solid fa-globe" /> 14 languages</div>
              <div className="vivi-empty-pill"><i className="fa-solid fa-shield-halved" /> Encrypted</div>
            </div>

            {/* Italic invite */}
            <div className="vivi-empty-invite">
              <span>"Because every touch, thought, and truth deserves Intimacy."</span>
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m) => (
            <motion.div
              key={m.id}
              id={`msg-${m.id}`}
              className={`msg-bubble-wrap ${m.role === 'user' ? 'user' : 'vivi'}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              {m.role === 'assistant' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div className="msg-label" style={{ marginBottom: 0 }}>VIVI</div>
                  <button
                    onClick={() => speakMessage(m)}
                    title="Listen"
                    data-testid={`speak-msg-${m.id}`}
                    style={{
                      background: 'none', border: '1px solid var(--border-soft)',
                      color: playingId === m.id ? 'var(--gold)' : 'var(--text-dim)',
                      width: 22, height: 22, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', cursor: 'pointer', fontSize: '0.67rem',
                    }}
                  >
                    <i className={`fa-solid ${playingId === m.id ? 'fa-pause' : 'fa-volume-high'}`} />
                  </button>
                  <button
                    onClick={() => saveToJournal(m)}
                    title={savedIds.has(m.id) ? 'Saved to Journal' : 'Save to Journal'}
                    data-testid={`save-msg-${m.id}`}
                    style={{
                      background: 'none', border: '1px solid var(--border-soft)',
                      color: savedIds.has(m.id) ? 'var(--gold)' : 'var(--text-dim)',
                      width: 22, height: 22, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', cursor: 'pointer', fontSize: '0.67rem',
                    }}
                  >
                    <i className={`fa-${savedIds.has(m.id) ? 'solid' : 'regular'} fa-bookmark`} />
                  </button>
                  <button
                    onClick={() => shareToWhatsApp(m)}
                    title="Share via WhatsApp"
                    data-testid={`share-msg-${m.id}`}
                    style={{
                      background: 'none', border: '1px solid var(--border-soft)',
                      color: '#25D366',
                      width: 22, height: 22, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', cursor: 'pointer', fontSize: '0.71rem',
                    }}
                  >
                    <i className="fa-brands fa-whatsapp" />
                  </button>
                </div>
              )}
              <div
                className={`msg-bubble ${m.role === 'user' ? 'user' : 'vivi'} vivi-formatted`}
                onClick={(e) => {
                  const link = e.target.closest('a.vivi-product-link');
                  if (link) {
                    // Fire-and-forget click track; the <a> tag's href + target still navigates.
                    try {
                      axios.post(`${API}/api/track`, {
                        event: 'vivi_product_click',
                        label: link.textContent || 'product',
                      }, { headers: authHeader() });
                    } catch (_) { /* ignore */ }
                  }
                }}
                dangerouslySetInnerHTML={{ __html: formatMsg(m.content) }}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {loading && (
          <div className="msg-bubble-wrap vivi">
            <div className="msg-label">VIVI</div>
            <div className="typing-wrap">
              <div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-area">
        {messages.length === 0 && (
          <>
            <div className="chips-eyebrow">
              I'm VIVI, your Personal Intimate Wellness Coach.<br />
              How can I help you today?
            </div>
            <div className="chips-scroll" style={{ padding: '0 4px 12px' }}>
              <div id="intimate-chips" className="chat-chips intimate-chips">
                {chips.map(c => (
                  <button key={c} className="chip intimate-chip" onClick={() => send(c)}>{c}</button>
                ))}
              </div>
            </div>
          </>
        )}
        <div className="chat-input-row">
          <textarea
            ref={textareaRef}
            className="chat-textarea"
            placeholder={transcribing ? 'Transcribing voice…' : recording ? 'Listening…' : 'Ask VIVI anything…'}
            value={input}
            onChange={e => { setInput(e.target.value); autoResize(); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            data-testid="chat-input"
            disabled={recording || transcribing}
          />
          <button
            className={`mic-btn${recording ? ' recording' : ''}`}
            onClick={recording ? stopRecording : startRecording}
            title={recording ? 'Stop & send' : 'Voice input'}
            data-testid="chat-mic-btn"
            disabled={transcribing}
          >
            <i className={`fa-solid ${recording ? 'fa-stop' : 'fa-microphone'}`} />
          </button>
          <button className="send-btn" onClick={() => send()} disabled={!input.trim() || loading} data-testid="chat-send">
            <i className="fa-solid fa-paper-plane" />
          </button>
        </div>
      </div>

      {/* Burn Confirm */}
      <AnimatePresence>
        {showBurnConfirm && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowBurnConfirm(false)}>
            <motion.div className="modal-sheet-center" style={{ margin: 'auto', padding: 28, border: '1px solid var(--border)', background: 'var(--bg2)', position: 'relative', zIndex: 1 }}
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <i className="fa-solid fa-fire" style={{ fontSize: '2.5rem', color: '#ef4444', display: 'block', marginBottom: 12 }} />
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginBottom: 8 }}>Burn this conversation?</div>
                <p style={{ fontSize: '0.89rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  This will permanently and irreversibly delete all messages. This cannot be undone.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn-outline w-full" onClick={() => setShowBurnConfirm(false)}>CANCEL</button>
                <button style={{ flex: 1, padding: '13px', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.81rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'var(--font-ui)' }} onClick={burnConversation} data-testid="confirm-burn">
                  DELETE
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Language picker */}
      <AnimatePresence>
        {showLangPicker && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowLangPicker(false)}>
            <motion.div className="modal-sheet"
              initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '20px 20px 8px', borderBottom: '1px solid var(--border-soft)' }}>
                <div className="eyebrow">Choose Language</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem' }}>VIVI speaks 14 languages</div>
              </div>
              <div className="lang-picker-list" style={{ padding: '8px 0 16px' }}>
                {LANGUAGES.map(l => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    data-testid={`lang-opt-${l.code}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', padding: '14px 20px',
                      background: language === l.code ? 'var(--gold-dim)' : 'transparent',
                      border: 'none', borderBottom: '1px solid var(--border-soft)',
                      color: language === l.code ? 'var(--gold)' : 'var(--text)',
                      cursor: 'pointer', fontSize: '0.96rem', fontFamily: 'var(--font-ui)',
                    }}
                  >
                    <span>{l.label}</span>
                    {language === l.code && <i className="fa-solid fa-check" />}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
